import qs from 'qs';
import { getClientEnvironment, Environment } from '@vp/digital-environment-lib';
import {
  partnerNavigationActionType,
  partnerNavigationActionProperties,
  GetStarted,
  Upgrade,
  UpgradePlan,
  BillingCyclePage,
  BillingCycleInternalPage,
  GetDomains,
  PurchaseDomain,
  ConnectDomain,
  TransferDomain,
  HowToConnectDomain,
  DomainWixRegistration,
  ReviewAndBuyDomain,
  ConnectDomainBuildWebsite,
} from '../enums/PartnerNavigationActionType';
import {
  API_BASE_URLS_PROD,
  API_BASE_URLS_QA,
  API_PARTNER_NAVIGATION_URLS_PROD,
} from '../enums/ApiBaseUrlsEnum';

export const WIX_LOGIN_ORIGIN = 'https://users.wix.com';
export const WIX_HOME_URL = 'https://www.wix.com';
export const WIX_SITE_THUMBNAIL_PATH = `${WIX_HOME_URL}/site-thumbnail`;
export const WIX_TERMS_OF_USE_URL = `${WIX_HOME_URL}/about/terms-of-use`;
export const WIX_PRIVACY_POLICY_URL = `${WIX_HOME_URL}/about/privacy`;
export const WIX_TERMS_OF_USE_PAGER_URL = `${WIX_TERMS_OF_USE_URL}/onepager`;
export const WIX_PRIVACY_POLICY_PAGER_URL = `${WIX_PRIVACY_POLICY_URL}-onepager`;
export const WIX_SUPPORT_URL = 'https://support.wix.com';
export const WIX_MULTILINGUAL_SUBDOMAIN_HELP_PAGE_PATH =
  '/article/wix-multilingual-about-multilingual-subdomains';
export const WIX_RESTRICTED_DOMAIN_EXTENSIONS_PATH =
  '/article/wix-account-country-limitations';

export const WIX_HOW_TO_CONNECT_DOMAIN_HELP_PAGE_PATH =
  '/article/connecting-a-domain-purchased-elsewhere';

export const WIX_FREE_ONE_YEAR_DOMAIN_VOUCHER_HELP_PAGE_PATH =
  '/article/using-your-free-1-year-domain-voucher';

export const VISTA_FREE_ONE_YEAR_DOMAIN_VOUCHER_HELP_PAGE_URL =
  'https://www.vistaprint.com/customer-care/help-center/24177337735309/';

export const HOW_TO_CREATE_A_WEBSITES_FOR_YOUR_BUSINESS_URL =
  'https://www.vistaprint.com/hub/how-to-design-a-website';

export const SMALL_BUSINESS_WEBSITE_DESIGN_URL =
  'https://www.vistaprint.com/hub/small-business-website-design-keys-to-success';

export const SEVEN_STUNNING_WEB_DESIGN_TRENDS =
  'https://www.vistaprint.com/hub/web-design-trends';

export const CHEAT_SHEET_FOR_SMALL_BUSINESS_SEO =
  '  https://www.vistaprint.com/hub/small-business-seo-guide';

export const WIX_NO_LANG_CODE = 'no';
export const HTTPS_PROTOCOL = 'https://';
export const WIX_POLICY_URL = '.wix.com/about/privacy';
export const WIX_TERMS_OF_USE_MULTILINGUAL_URL = '.wix.com/about/terms-of-use';

export function getPartnerNavigationOrigin(
  environment: Environment = getClientEnvironment(),
  queryString: string
) {
  if (
    environment === Environment.Staging ||
    environment === Environment.Local
  ) {
    return API_BASE_URLS_QA.PARTNER_NAVIGATION_SERVICE;
  } else {
    const parsedQuery = qs.parse(queryString);
    // default to US if locale is null or empty
    const locale = (parsedQuery.locale as string) || 'en-US';
    if (locale in API_PARTNER_NAVIGATION_URLS_PROD) {
      return API_PARTNER_NAVIGATION_URLS_PROD[locale];
      // default to US if locale is not supported yet
    } else return API_PARTNER_NAVIGATION_URLS_PROD['en-US'];
  }
}

export function replaceNOLanguageCodeWix(languageCode: string) {
  if (languageCode === 'nb') {
    languageCode = WIX_NO_LANG_CODE;
  }
  return languageCode;
}

export function getFree1YearDomainVoucherUrl(languageCode: string) {
  languageCode = replaceNOLanguageCodeWix(languageCode);
  if (languageCode == 'en') {
    return VISTA_FREE_ONE_YEAR_DOMAIN_VOUCHER_HELP_PAGE_URL;
  }
  return `${WIX_SUPPORT_URL}/${languageCode}${WIX_FREE_ONE_YEAR_DOMAIN_VOUCHER_HELP_PAGE_PATH}`;
}

export function getHowToCreateWebsiteUrl(languageCode: string) {
  if (languageCode === 'en') {
    return HOW_TO_CREATE_A_WEBSITES_FOR_YOUR_BUSINESS_URL;
  }
}
export function getSmallBusinessWebsiteUrl(languageCode: string) {
  if (languageCode === 'en') {
    return SMALL_BUSINESS_WEBSITE_DESIGN_URL;
  }
}
export function getSevenStunningWebDesignTrendsUrl(languageCode: string) {
  if (languageCode === 'en') {
    return SEVEN_STUNNING_WEB_DESIGN_TRENDS;
  }
}

export function getCheatSheetForSmallBusinessSeoUrl(languageCode: string) {
  if (languageCode === 'en') {
    return CHEAT_SHEET_FOR_SMALL_BUSINESS_SEO;
  }
}

export function getWixMultilingualSubdomainHelpUrl(languageCode: string) {
  const wix_pending_localizations = ['da', 'sv'];
  languageCode = replaceNOLanguageCodeWix(languageCode);
  if (wix_pending_localizations.includes(languageCode)) {
    languageCode = 'en'; //setting to english
  }
  return `${WIX_SUPPORT_URL}/${languageCode}${WIX_MULTILINGUAL_SUBDOMAIN_HELP_PAGE_PATH}`;
}

export function getWixRestrictedDomainExtensionsUrl(languageCode: string) {
  languageCode = languageCode === 'sv' ? 'en' : languageCode;
  languageCode = replaceNOLanguageCodeWix(languageCode);
  return `${WIX_SUPPORT_URL}/${languageCode}${WIX_RESTRICTED_DOMAIN_EXTENSIONS_PATH}`;
}

export function getWixHowToConnectDomainHelpUrl(languageCode: string) {
  languageCode = replaceNOLanguageCodeWix(languageCode);
  return `${WIX_SUPPORT_URL}/${languageCode}${WIX_HOW_TO_CONNECT_DOMAIN_HELP_PAGE_PATH}`;
}

export function getWixPrivacyPolicyUrl(languageCode: string) {
  languageCode = replaceNOLanguageCodeWix(languageCode);
  return `${HTTPS_PROTOCOL}${languageCode}${WIX_POLICY_URL}`;
}

export function getWixTermsOfUseUrl(languageCode: string) {
  languageCode = replaceNOLanguageCodeWix(languageCode);
  return `${HTTPS_PROTOCOL}${languageCode}${WIX_TERMS_OF_USE_MULTILINGUAL_URL}`;
}

export function getPartnerIntegrationOrigin(
  environment: Environment = getClientEnvironment()
) {
  return environment === Environment.Staging ||
    environment === Environment.Local
    ? API_BASE_URLS_QA.PARTNER_INTEGRATION_SERVICE
    : API_BASE_URLS_PROD.PARTNER_INTEGRATION_SERVICE;
}

export function getDiwyOrigin(
  environment: Environment = getClientEnvironment()
) {
  return environment === Environment.Staging ||
    environment === Environment.Local
    ? API_BASE_URLS_QA.DIWY
    : API_BASE_URLS_PROD.DIWY;
}

export function getWixGetStartedNavigationUrl(
  locale: string,
  requestor: string = 'Digital',
  environment?: Environment,
  source?: string
): string {
  return getWixNavigationUrl(
    GetStarted,
    locale,
    requestor,
    environment,
    source
  );
}

export function getWixSitesUpgradeUrl(
  locale: string,
  siteId: string,
  requestor: string = 'Digital',
  environment?: Environment,
  source?: string
): string {
  const queryString = qs.stringify({
    locale,
    requestor,
    siteId,
    source,
  });
  return getOutboundNavigationPartnerUrl(Upgrade, queryString, environment);
}

export function getWixVistaBillingUpgradePlanUrl(
  locale: string,
  metaSiteId: string,
  requestor: string = 'Digital',
  environment?: Environment,
  source?: string
) {
  const action = partnerNavigationActionType.UpgradePlan;
  const queryString = qs.stringify({
    locale,
    requestor,
    metaSiteId,
    source,
  });
  return getInboundNavigationPartnerUrl(action, queryString, environment);
}

export function getWixVistaUpgradeBillingCycleUrl(
  locale: string,
  subscriptionGroupAltId: string,
  selectedPlan: string,
  requestor: string = 'Digital',
  environment?: Environment,
  source?: string
) {
  const action = partnerNavigationActionType.BillingCyclePage;
  const queryString = qs.stringify({
    locale,
    requestor,
    subscriptionGroupAltId,
    selectedPlan,
    source,
  });
  return getInboundNavigationPartnerUrl(action, queryString, environment);
}

export function getWixVistaInternalUpgradeBillingCycleUrl(
  locale: string,
  subscriptionGroupAltId: string,
  selectedPlan: string,
  requestor: string = 'Digital',
  environment?: Environment,
  source?: string
) {
  const queryString = qs.stringify({
    locale,
    requestor,
    subscriptionGroupAltId,
    selectedPlan,
    source,
  });
  return getOutboundNavigationPartnerUrl(
    BillingCycleInternalPage,
    queryString,
    environment
  );
}

export function getWixProductsApiUrl(environment?: Environment): string {
  return getPartnerIntegrationOrigin(environment) + '/api/v1/products';
}

export function getWixWorkspaceApiUrl(
  shopperId: string,
  locale: string,
  environment?: Environment
): string {
  return `${getPartnerIntegrationOrigin(
    environment
  )}/api/v1/standardCards/${shopperId}/${locale}`;
}

export function getWixLoginPage(): string {
  return WIX_LOGIN_ORIGIN + '/signin';
}

function getWixNavigationUrl(
  actionProperty: partnerNavigationActionProperties,
  locale: string,
  requestor: string,
  environment: Environment,
  source?: string,
  acceptTerms?: string
): string {
  const queryString = qs.stringify({
    locale,
    requestor,
    source,
    acceptTerms,
  });

  return getOutboundNavigationPartnerUrl(
    actionProperty,
    queryString,
    environment
  );
}

function getOutboundNavigationPartnerUrl(
  actionProperty: partnerNavigationActionProperties,
  queryString: string,
  environment: Environment
): string {
  return `${getPartnerNavigationOrigin(
    environment,
    queryString
  )}/navigate/outbound/${actionProperty.productType}/wix/${
    actionProperty.value
  }?${queryString}`;
}

function getInboundNavigationPartnerUrl(
  action: string,
  queryString: string,
  environment: Environment
): string {
  return `${getPartnerNavigationOrigin(
    environment,
    queryString
  )}/navigate/inbound/secure/wix/${action}?${queryString}`;
}

function getInternalNavigationPartnerUrl(
  action: string,
  queryString: string,
  environment: Environment
): string {
  return `${getPartnerNavigationOrigin(
    environment,
    queryString
  )}/navigate/internal/secure/WIX/${action}?${queryString}`;
}

export function getWixGetStartedTnCAcceptedNavigationUrl(
  locale: string,
  requestor: string = 'Digital',
  environment?: Environment,
  source?: string
): string {
  const acceptTerms = 'true';
  return getWixNavigationUrl(
    GetStarted,
    locale,
    requestor,
    environment,
    source,
    acceptTerms
  );
}

function getWixDomainFlowNavigationUrl(
  locale: string,
  actionProperty: partnerNavigationActionProperties,
  requestor: string,
  domainName?: string,
  siteId?: string,
  environment?: Environment,
  source?: string,
  hasOwnDomain?: boolean
): string {
  const acceptTerms = 'true';
  const queryString = qs.stringify(
    {
      locale,
      requestor,
      acceptTerms,
      domainName,
      siteId,
      source,
      hasOwnDomain,
    },
    { skipNulls: true }
  );
  return getOutboundNavigationPartnerUrl(
    actionProperty,
    queryString,
    environment
  );
}

//TODO: This link would be unused post Wix Domains Release 2 launch
export function getWixDomainsNavigationUrl(
  locale: string,
  requestor: string = 'Digital',
  environment?: Environment,
  source?: string
): string {
  const acceptTerms = 'true';
  return getWixNavigationUrl(
    GetDomains,
    locale,
    requestor,
    environment,
    source,
    acceptTerms
  );
}

export function getWixHowToConnectDomainNavigationUrl(
  locale: string,
  requestor: string = 'Digital',
  environment?: Environment,
  source?: string
): string {
  const acceptTerms = 'true';
  return getWixNavigationUrl(
    HowToConnectDomain,
    locale,
    requestor,
    environment,
    source,
    acceptTerms
  );
}

export function getWixPurchaseDomainNavigationUrl(
  locale: string,
  requestor: string = 'Digital',
  domainName: string,
  siteId?: string,
  environment?: Environment,
  source?: string
): string {
  return getWixDomainFlowNavigationUrl(
    locale,
    PurchaseDomain,
    requestor,
    domainName,
    siteId,
    environment,
    source
  );
}

export function getWixTransferDomainNavigationUrl(
  locale: string,
  requestor: string = 'Digital',
  domainName: string,
  siteId?: string,
  environment?: Environment,
  source?: string
): string {
  return getWixDomainFlowNavigationUrl(
    locale,
    TransferDomain,
    requestor,
    domainName,
    siteId,
    environment,
    source
  );
}

export function getWixConnectDomainNavigationUrl(
  locale: string,
  requestor: string = 'Digital',
  domainName: string,
  siteId?: string,
  environment?: Environment,
  source?: string
): string {
  return getWixDomainFlowNavigationUrl(
    locale,
    ConnectDomain,
    requestor,
    domainName,
    siteId,
    environment,
    source
  );
}

export function getWixReviewAndBuyDomainNavigationUrl(
  locale: string,
  requestor: string = 'Digital',
  domainName: string,
  environment?: Environment,
  source?: string,
  siteId?: string,
  hasOwnDomain?: boolean
): string {
  return getWixDomainFlowNavigationUrl(
    locale,
    ReviewAndBuyDomain,
    requestor,
    domainName,
    siteId,
    environment,
    source,
    hasOwnDomain
  );
}

export function getWixConnectDomainBuildWebsiteNavigationUrl(
  locale: string,
  requestor: string = 'Digital',
  domainName: string,
  environment?: Environment,
  source?: string,
  siteId?: string,
  hasOwnDomain?: boolean
): string {
  return getWixDomainFlowNavigationUrl(
    locale,
    ConnectDomainBuildWebsite,
    requestor,
    domainName,
    siteId,
    environment,
    source,
    hasOwnDomain
  );
}

export function getDomainRegistrationNavigationUrl(
  locale: string,
  requestor: string = 'Digital',
  siteId?: string,
  environment?: Environment,
  source?: string
): string {
  const acceptTerms = 'true';
  const queryString = qs.stringify({
    locale,
    requestor,
    acceptTerms,
    siteId,
    source,
  });
  return getInternalNavigationPartnerUrl(
    partnerNavigationActionType.DomainWixRegistration,
    queryString,
    environment
  );
}
